/**
 * returnTo — where to land a user after sign-in.
 *
 * Portal students should return to the deep link they actually wanted (or the
 * portal home), not the legacy IDE dashboard. Auth guards stash the attempted
 * path as `?returnTo=`; Login threads it through the OIDC round-trip as
 * `state.returnTo`; AuthCallback and the /login authed-redirect honour it.
 *
 * `sanitizeReturnTo` is the trust boundary: only same-origin, absolute in-app
 * paths are allowed, so a crafted `?returnTo=//evil.com` or `javascript:` can
 * never turn sign-in into an open redirect.
 */

export const POST_LOGIN_DEFAULT = '/portal';

/** Paths we never bounce back into after login (they'd re-trigger a redirect). */
const DISALLOWED_PREFIXES = ['/login', '/auth/callback'];

/** True if the string contains any ASCII control character (0x00–0x1F). */
function hasControlChar(value: string): boolean {
  for (let i = 0; i < value.length; i++) {
    if (value.charCodeAt(i) < 0x20) return true;
  }
  return false;
}

/**
 * Return a safe in-app path, or null if `raw` is missing/unsafe.
 * Safe = starts with a single '/', not '//' (protocol-relative), no backslash
 * or control chars, and not one of the auth routes.
 */
export function sanitizeReturnTo(raw: string | null | undefined): string | null {
  if (!raw) return null;
  let value = raw;
  try {
    // Tolerate a single layer of encoding from the query string.
    value = decodeURIComponent(raw);
  } catch {
    value = raw;
  }
  if (!value.startsWith('/')) return null;
  if (value.startsWith('//')) return null; // protocol-relative → off-origin
  if (value.includes('\\')) return null; // backslash tricks
  if (hasControlChar(value)) return null;
  if (
    DISALLOWED_PREFIXES.some(
      (p) => value === p || value.startsWith(`${p}/`) || value.startsWith(`${p}?`),
    )
  ) {
    return null;
  }
  return value;
}

/** The post-login destination: a sanitized returnTo, or the portal default. */
export function resolvePostLogin(raw: string | null | undefined): string {
  return sanitizeReturnTo(raw) ?? POST_LOGIN_DEFAULT;
}

/** Build a `/login?returnTo=…` href from a full path (pathname+search+hash). */
export function loginHrefFor(fullPath: string): string {
  const safe = sanitizeReturnTo(fullPath);
  return safe ? `/login?returnTo=${encodeURIComponent(safe)}` : '/login';
}
